import {useMutation, useQuery} from '@apollo/client/react';
import {graphql} from '../gqlApp';
import {useCompanyActivityRefetch} from './activity';
import {API_BASE} from './endpoints';
import {getToken} from '../utils/auth';

export type LegalPdfInput = {
	companyId: string;
	kind: string;
	title: string;
	scope?: string;
	warehouseId?: string;
	snapshotId?: string;
	editionId?: string | null;
	checksum?: string;
};

export function legalPdfUrl(input: LegalPdfInput) {
	const query = new URLSearchParams({format: 'pdf'});
	if (input.scope) query.set('scope', input.scope);
	if (input.warehouseId) query.set('warehouseId', input.warehouseId);
	if (input.snapshotId) query.set('snapshotId', input.snapshotId);
	if (input.editionId) query.set('editionId', input.editionId);
	return `${API_BASE}/api/legal/documents/${input.companyId}/${input.kind}?${query}`;
}

const pdfCache = new Map<string, Promise<ArrayBuffer>>();
export function fetchLegalPdf(url: string, checksum = '') {
	const token = getToken();
	const key = `${token}:${url}:${checksum}`;
	const existing = pdfCache.get(key);
	if (existing) return existing;
	const pending = fetch(url, {
		headers: {
			Authorization: `Bearer ${token}`,
			...(checksum ? {'X-Legal-Document-Checksum': checksum} : {}),
		},
	}).then(async (response) => {
		if (!response.ok) throw new Error('Не удалось загрузить PDF документа');
		return response.arrayBuffer();
	});
	pdfCache.set(key, pending);
	pending.catch(() => pdfCache.delete(key));
	if (pdfCache.size > 16) pdfCache.delete(pdfCache.keys().next().value!);
	return pending;
}

export async function downloadLegalDocument(input: LegalPdfInput) {
	const bytes = await fetchLegalPdf(legalPdfUrl(input), input.checksum);
	const url = URL.createObjectURL(new Blob([bytes], {type: 'application/pdf'}));
	const link = document.createElement('a');
	link.href = url;
	link.download = `${input.title}.pdf`;
	link.click();
	window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const LEGAL_DOCUMENTS_QUERY = graphql(`
	query LegalDocuments($input: LegalContextInput!) {
		legalDocuments(input: $input) {
			signingChallengeId
			connectionId
			acceptedAt
			receiptStatus
			receiptPreview
			authorityBasis
			checksum
			dealId
			signed
			documents {
				editionId
				pdfChecksum
				acceptanceId
				kind
				title
				version
				html
				checksum
			}
		}
	}
`);
export const REQUEST_LEGAL_CODE = graphql(`
	mutation RequestLegalCode($input: RequestLegalCodeInput!) {
		requestLegalSigningCode(input: $input) {
			verificationId
			challengeId
			channel
			resendTimeout
			devCode
		}
	}
`);
export const SIGN_LEGAL_DOCUMENTS = graphql(`
	mutation SignLegalDocuments($input: SignLegalDocumentsInput!) {
		signLegalDocuments(input: $input) {
			signingChallengeId
			connectionId
			acceptedAt
			receiptStatus
			checksum
			dealId
			signed
			documents {
				editionId
				pdfChecksum
				acceptanceId
				kind
				title
				version
				html
				checksum
			}
		}
	}
`);
export const LEGAL_TERMS_QUERY = graphql(`
	query WarehouseLegalTerms($warehouseId: ID!) {
		warehouseLegalTerms(warehouseId: $warehouseId)
	}
`);
export const SAVE_LEGAL_TERMS = graphql(`
	mutation SaveLegalTerms($input: WarehouseLegalTermsInput!) {
		saveWarehouseLegalTerms(input: $input)
	}
`);
export const useLegalDocuments = (
	input: {companyId: string; warehouseId?: string; scope: string},
	skip = false,
) => {
	useCompanyActivityRefetch(input.companyId, [
		'LegalDocuments',
		'LegalHistory',
		'PendingPlatformRules',
	]);
	return useQuery(LEGAL_DOCUMENTS_QUERY, {variables: {input}, skip});
};
export const useRequestLegalCode = () => useMutation(REQUEST_LEGAL_CODE);
export const useSignLegalDocuments = () => useMutation(SIGN_LEGAL_DOCUMENTS);

export function legalDeviceId() {
	const key = 'mpgo-legal-device';
	let id = localStorage.getItem(key);
	if (!id) {
		id = crypto.randomUUID();
		localStorage.setItem(key, id);
	}
	return id;
}
